import { useRef, useMemo, useEffect } from "react";
import { ESP32_EYES_PRESETS } from "../../assets/esp32_eyes_presets";
import { normalizeEmotion } from "../../utils/emotion";
import { clamp, hash01, lerp } from "./chunk-0.jsx";
import { ESP_OLED_EYE_SIZE, espCanvasProfile } from "./chunk-1.jsx";
import { drawEspEye } from "./chunk-2.jsx";
import { espPresetPairFromEmotion } from "./chunk-3.jsx";
import { ESP32_EXPRESSION_PRESETS } from "./chunk-17.jsx";
export function Esp32Eyes({
  emotion,
  expression,
  variant,
  poke = 0,
  heartbeatRef,
  active = true,
  motionBoost = false
}) {
  const canvasRef = useRef(null);
  const presetPair = useMemo(() => {
    if (expression && ESP32_EXPRESSION_PRESETS[expression]) {
      return ESP32_EXPRESSION_PRESETS[expression];
    }
    return espPresetPairFromEmotion(emotion, variant);
  }, [emotion, variant, expression]);
  const leftCfg = ESP32_EYES_PRESETS?.[presetPair?.left];
  const rightCfg = ESP32_EYES_PRESETS?.[presetPair?.right] || leftCfg;
  const espProfile = useMemo(() => espCanvasProfile(variant), [variant]);
  const canvasW = espProfile.w;
  const canvasH = espProfile.h;
  const eyeSize = espProfile.eyeSize;
  const eyeGap = espProfile.eyeGap;
  const scaleFactor = eyeSize / ESP_OLED_EYE_SIZE;
  const presetKey = `${presetPair?.left || ''}|${presetPair?.right || ''}|${variant || ''}`;
  const seed = useMemo(() => hash01(presetKey), [presetKey]);
  const pokeRef = useRef(poke);
  useEffect(() => {
    pokeRef.current = poke;
  }, [poke]);
  const stateRef = useRef({
    lastPaintT: 0,
    lastPoke: 0,
    forceBlinkAt: 0
  });
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = false;
    const img = ctx.createImageData(canvasW, canvasH);
    const mono = new Uint8Array(canvasW * canvasH);
    const s = stateRef.current;
    let raf = 0;
    const loop = t => {
      heartbeatRef.current = Date.now();

      // ~30fps
      if (s.lastPaintT && t - s.lastPaintT < 33) {
        raf = requestAnimationFrame(loop);
        return;
      }
      s.lastPaintT = t;
      mono.fill(0);

      // LookAssistant-like transform (normalized x/y in [-1..1])
      const lookX = clamp(Math.sin(t / 1100 + seed * 10) * 0.6 + Math.sin(t / 3200 + seed * 4) * 0.4, -1, 1);
      const lookY = clamp(Math.sin(t / 1400 + seed * 6 + 1.2) * 0.7 + Math.sin(t / 2600 + seed * 3) * 0.3, -1, 1);
      const moveX = Math.round(-25 * scaleFactor * lookX);
      const moveY = Math.round(20 * scaleFactor * lookY);
      const scaleYy = 1.0 - Math.abs(lookY) * 0.4;
      const scaleYRight = (1.0 - lookX * 0.2) * scaleYy;
      const scaleYLeft = (1.0 + lookX * 0.2) * scaleYy;
      const cx0 = Math.round(canvasW / 2);
      const cy0 = Math.round(canvasH / 2);
      const cxL = cx0 - Math.round(eyeSize / 2) - eyeGap;
      const cxR = cx0 + Math.round(eyeSize / 2) + eyeGap;
      const blinkPeriod = (motionBoost ? 1600 : 2600) + seed * (motionBoost ? 900 : 1600);
      const blinkWindow = motionBoost ? 320 : 260;
      const blinkPhase = (t + seed * blinkPeriod) % blinkPeriod;
      let blink = 0;
      if (blinkPhase > blinkPeriod - blinkWindow) {
        const p = (blinkPhase - (blinkPeriod - blinkWindow)) / blinkWindow;
        blink = Math.sin(p * Math.PI);
      }
      const pokeNow = pokeRef.current || 0;
      if (pokeNow !== s.lastPoke) {
        s.lastPoke = pokeNow;
        s.forceBlinkAt = t;
      }
      if (s.forceBlinkAt) {
        const p = clamp((t - s.forceBlinkAt) / 260, 0, 1);
        blink = Math.max(blink, Math.sin(p * Math.PI));
        if (p >= 1) s.forceBlinkAt = 0;
      }
      const blinkT = clamp(blink, 0, 1) ** 2;
      const blinkWidth = Math.round(60 * scaleFactor);
      const blinkHeight = Math.max(1, Math.round(2 * scaleFactor));
      const scaleDown = 1 - blinkT;
      const buildCfg = (cfg, isRight, eyeScaleY) => {
        if (!cfg) return null;
        const slopeSign = isRight ? -1 : 1;
        const rawW = (cfg.Width ?? ESP_OLED_EYE_SIZE) * scaleFactor;
        const rawH = (cfg.Height ?? ESP_OLED_EYE_SIZE) * scaleFactor * eyeScaleY;
        return {
          OffsetX: Math.round((isRight ? 1 : -1) * (cfg.OffsetX ?? 0) * scaleFactor + moveX),
          OffsetY: Math.round(-((cfg.OffsetY ?? 0) * scaleFactor) - moveY),
          Width: Math.round(lerp(rawW, blinkWidth, blinkT)),
          Height: Math.round(lerp(rawH, blinkHeight, blinkT)),
          Slope_Top: (cfg.Slope_Top ?? 0) * slopeSign * scaleDown,
          Slope_Bottom: (cfg.Slope_Bottom ?? 0) * slopeSign * scaleDown,
          Radius_Top: Math.round((cfg.Radius_Top ?? 0) * scaleFactor * scaleDown),
          Radius_Bottom: Math.round((cfg.Radius_Bottom ?? 0) * scaleFactor * scaleDown)
        };
      };
      const l = buildCfg(leftCfg, false, scaleYLeft);
      const r = buildCfg(rightCfg, true, scaleYRight);
      if (l) drawEspEye(mono, canvasW, canvasH, cxL, cy0, l);
      if (r) drawEspEye(mono, canvasW, canvasH, cxR, cy0, r);
      const data = img.data;
      for (let i = 0; i < mono.length; i++) {
        const on = mono[i] ? 255 : 0;
        const o = i * 4;
        data[o] = on;
        data[o + 1] = on;
        data[o + 2] = on;
        data[o + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
      if (!l || !r) {
        ctx.save();
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        ctx.fillText('Missing esp32-eyes preset', 6, 12);
        ctx.restore();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, leftCfg, rightCfg, canvasW, canvasH, eyeSize, eyeGap, scaleFactor, heartbeatRef, seed, motionBoost]);
  return <div className="w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} width={canvasW} height={canvasH} className="block w-full h-auto" style={{
      imageRendering: 'pixelated'
    }} />
    </div>;
}
export function styleForIrisEyes(emotion) {
  const e = normalizeEmotion(emotion);
  switch (e) {
    case 'sleepy':
      return {
        openness: 0.18,
        pupil: 0.7,
        brow: 0.0,
        sleepy: true
      };
    case 'sad':
    case 'crying':
      return {
        openness: 0.55,
        pupil: 0.95,
        brow: -0.18,
        tear: true
      };
    case 'surprised':
    case 'shocked':
      return {
        openness: 1.12,
        pupil: 0.6,
        brow: -0.22
      };
    case 'angry':
      return {
        openness: 0.72,
        pupil: 0.72,
        brow: 0.42
      };
    case 'loving':
      return {
        openness: 0.95,
        pupil: 1.2,
        brow: -0.06,
        blush: true,
        iris: '#fb7185'
      };
    case 'confused':
      return {
        openness: 0.86,
        pupil: 0.85,
        brow: 0.12,
        asym: true
      };
    case 'thinking':
      return {
        openness: 0.86,
        pupil: 0.82,
        brow: 0.06
      };
    case 'relaxed':
      return {
        openness: 0.62,
        pupil: 0.85,
        brow: -0.08
      };
    case 'cool':
      return {
        openness: 0.75,
        pupil: 0.78,
        brow: -0.02,
        shades: true
      };
    case 'winking':
      return {
        openness: 0.92,
        pupil: 0.88,
        brow: -0.06,
        wink: true
      };
    case 'embarrassed':
      return {
        openness: 0.9,
        pupil: 1.05,
        brow: -0.04,
        blush: true
      };
    case 'silly':
      return {
        openness: 0.96,
        pupil: 1.18,
        brow: 0.04,
        cross: true
      };
    case 'confident':
      return {
        openness: 0.82,
        pupil: 0.82,
        brow: 0.18,
        asym: true
      };
    case 'delicious':
      return {
        openness: 1.02,
        pupil: 0.88,
        brow: -0.12,
        drool: true
      };
    case 'happy':
      return {
        openness: 0.88,
        pupil: 0.9,
        brow: -0.1
      };
    case 'laughing':
      return {
        openness: 0.68,
        pupil: 0.72,
        brow: -0.22,
        jiggle: true
      };
    case 'neutral':
    default:
      return {
        openness: 0.84,
        pupil: 0.84,
        brow: 0.0
      };
  }
}
